import { ThirdPartyDraggable } from '@fullcalendar/interaction';
import React, { useEffect } from 'react';
import { halfHour } from 'shared/constants';
import { ExtendedProps } from 'shared/lib/full-calendar';
import { DataType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';
import { WeekDays } from 'shared/types/week-days';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

export type Options = {
  listRef: React.RefObject<HTMLDivElement> | undefined;
  tasks: Task[];
  weekStartsOn: WeekDays;
  includeDuration?: boolean;
  includeDueDateAsOriginDate?: boolean;
};

export const useRegisterFullCalendarDraggableItems = ({
  listRef,
  tasks,
  weekStartsOn,
  includeDuration,
  includeDueDateAsOriginDate,
}: Options) =>
  useEffect(() => {
    if (!listRef?.current) {
      return;
    }

    const thirdPartyDraggable = new ThirdPartyDraggable(listRef.current, {
      // eslint-disable-next-line max-len -- long selector
      itemSelector: `
       [role="treeitem"]:has([data-component-type="${DataType.Task}"]),
       [role="treeitem"]:has([data-component-type="${DataType.Goal}"]),
       [role="treeitem"]:has([data-component-type="${DataType.LifeArea}"])
     `,
      eventData: (el) => {
        const dataEl = el.querySelector('[data-component-type]');
        const id = dataEl?.getAttribute('data-id');
        const task = tasks.find((task) => task.id === id);
        const title = dataEl?.getAttribute('data-title');
        const duration = dataEl?.getAttribute('data-duration') ?? halfHour;
        const componentType = dataEl?.getAttribute(
          'data-component-type',
        ) as DataType; // Cast to DataType

        if (!id) {
          return;
        }

        // Prepare extendedProps based on the component type
        const extendedProps: ExtendedProps = {
          weekStartsOn,
          task,
        };

        switch (componentType) {
          case DataType.Task:
            extendedProps.taskId = id;
            break;
          case DataType.Goal:
            extendedProps.goalId = id;
            break;
          case DataType.LifeArea:
            extendedProps.lifeAreaId = id;
            break;
        }

        // for allowing dragging of the task from the list onto a due date, we get the due task planning as origin date.
        if (includeDueDateAsOriginDate && task) {
          const duePlanning = getDueTaskPlanning(task, {
            date: new Date(),
            today: new Date(),
            weekStartsOn,
            uncompletedOnly: true,
          });
          extendedProps.originDate = duePlanning[duePlanning.length - 1].date;
        }

        const eventBase = {
          id,
          title,
          extendedProps,
          create: true,
        };

        if (includeDuration) {
          return {
            ...eventBase,
            duration: { ms: duration },
          };
        }

        return eventBase;
      },
    });

    return () => thirdPartyDraggable?.destroy();
  }, [
    includeDuration,
    includeDueDateAsOriginDate,
    listRef,
    tasks,
    weekStartsOn,
  ]);
